import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import Confetti from "react-confetti";
import { useWindowWidth } from "@react-hook/window-size";
import CloseIcon from "@mui/icons-material/Close";
import {
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentFieldRowTitle,
  UpfrontPaymentInfoRow,
  UpfrontPaymentButtonRow,
  UpfrontPaymentReceiptTitle,
  UpfrontPaymentReceiptHeader,
  UpfrontPaymentReceiptThankYou,
  UpfrontPaymentReceiptWrapper,
  UpfrontPaymentReceiptMessage,
  UpfrontPaymentFieldRow,
  UpfrontPaymentCost,
  UpfrontPaymentInfoBillContent,
  UpfrontPaymentMainText,
  UpfrontPaymentPlanTitle,
  UpfrontPaymentSolidLine,
  UpfrontPaymentCloseIcon,
} from "./UpfrontPayment.styles";

import { useDeviceHook } from "../../utils/hooks/useDeviceHook";

import { GetTypographyContent } from "../shared/Typography/Typography";
import { Button } from "../shared/Button/Button";

import {
  GetUpfrontPaymentPlan,
  GetUpfrontPaymentReceipt,
  UpfrontPaymentPlan,
  UpfrontPaymentReceiptType,
  getCentsToDollars,
} from "./Services/UpfrontPaymentService";

import { GreenFullCheckIcon } from "../../assets/iconComponents/GreenFullCheckIcon";
import { themePalette } from "../../theme";
import { EOBIcon } from "../../assets/iconComponents/EOBIcon";
import { UpfrontPaymentPlanSteps } from "./UpfrontPaymentPlanSteps";
import { StepperIndex } from "./types";
import { BabyPeekIntroFAQAccordionWrapper } from "./PaymentPlanNumPayments.styles";

const RECEIPT_FETCH_MAX_ATTEMPTS = 3;
const RECEIPT_FETCH_RETRY_DELAY_MS = 500;

async function fetchReceiptWithNetworkRetry(
  billingPK: string,
  adminID?: string,
  isCancelled: () => boolean = () => false,
): Promise<UpfrontPaymentReceiptType | undefined> {
  for (let attempt = 1; attempt <= RECEIPT_FETCH_MAX_ATTEMPTS; attempt += 1) {
    if (isCancelled()) {
      return undefined;
    }

    let receiptResponse: Response;
    try {
      receiptResponse = await GetUpfrontPaymentReceipt(
        billingPK,
        undefined,
        adminID,
      );
    } catch (error) {
      if (
        !(error instanceof TypeError) ||
        attempt === RECEIPT_FETCH_MAX_ATTEMPTS
      ) {
        throw error;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, RECEIPT_FETCH_RETRY_DELAY_MS * attempt);
      });
      continue;
    }

    if (!receiptResponse.ok) {
      throw new Error(`Receipt request failed with ${receiptResponse.status}`);
    }
    return (await receiptResponse.json()) as UpfrontPaymentReceiptType;
  }

  throw new Error("Receipt request exhausted all attempts");
}

export const UpfrontPaymentReceipt: React.FC<{
  isPaymentPlan: boolean;
  paymentAmount: number;
  modal?: boolean;
  setModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveStep: React.Dispatch<React.SetStateAction<StepperIndex>>;
  receiptData?: UpfrontPaymentReceiptType | null;
}> = ({
  modal,
  setModal,
  isPaymentPlan,
  setActiveStep,
  paymentAmount,
  receiptData,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();

  const [finalReceipt, setFinalReceipt] = useState<UpfrontPaymentReceiptType>();
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();
  const [showNotice, setShowNotice] = useState<boolean>(false);
  const [paymentPlan, setPaymentPlan] = useState<UpfrontPaymentPlan[]>();
  const [numberPayments, setNumberPayments] = useState<"6">();
  const windowWidth = useWindowWidth();

  useEffect(() => {
    let isCancelled = false;

    if (!isPaymentPlan) {
      if (receiptData) {
        // Use provided receipt data (e.g., from payoff response)
        setFinalReceipt(receiptData);
      } else {
        // Fetch receipt data from API
        const getReceipt = async () => {
          if (billingPK) {
            try {
              const receiptJson = await fetchReceiptWithNetworkRetry(
                billingPK,
                adminID,
                () => isCancelled,
              );
              if (!isCancelled && receiptJson) {
                setFinalReceipt(receiptJson);
              }
            } catch (error) {
              if (!isCancelled) {
                setFinalReceipt(undefined);
              }
            }
          }
        };
        getReceipt();
      }
    }

    return () => {
      isCancelled = true;
    };
  }, [receiptData, isPaymentPlan, billingPK, adminID]);

  useEffect(() => {
    if (isPaymentPlan) {
      const getPaymentPlan = async () => {
        try {
          const paymentPlanResponse = await GetUpfrontPaymentPlan(
            billingPK,
            adminID,
          );
          const paymentPlanJson = await paymentPlanResponse.json();
          setPaymentPlan(paymentPlanJson);
          if (paymentPlanJson) {
            setNumberPayments(paymentPlanJson.length.toString());
          }
        } catch (error) {
          setPaymentPlan(undefined);
          setNumberPayments(undefined);
        }
      };
      getPaymentPlan();
    } else {
      // Clear payment plan data when not a payment plan
      setPaymentPlan(undefined);
      setNumberPayments(undefined);
    }
  }, [isPaymentPlan, billingPK, adminID]);

  const getConfettiHeight = (): number => {
    if (isPaymentPlan) {
      return 550;
    }
    if (deviceType !== "mobile") {
      return 375;
    }

    return 450;
  };

  return (
    <div style={{ margin: "0px 20px 20px" }}>
      {modal && !showNotice && (
        <Confetti
          height={getConfettiHeight()}
          numberOfPieces={100}
          gravity={0.1}
          initialVelocityX={5}
          initialVelocityY={5}
          recycle
          colors={[
            themePalette.blueMedium,
            themePalette.greenMedium,
            themePalette.blueLight,
            themePalette.greenLight,
          ]}
          width={deviceType !== "mobile" ? 525 : windowWidth - 25}
        />
      )}
      <UpfrontPaymentReceiptWrapper
        deviceType={deviceType}
        noConfetti={!modal || showNotice}
        isPaymentPlan={isPaymentPlan}
      >
        {modal ? (
          <>
            <UpfrontPaymentCloseIcon>
              <CloseIcon onClick={() => setModal && setModal(false)} />
            </UpfrontPaymentCloseIcon>
            <UpfrontPaymentReceiptHeader>
              {showNotice ? (
                <UpfrontPaymentReceiptThankYou>
                  {t("upfrontPayment.receipt.notice")}
                </UpfrontPaymentReceiptThankYou>
              ) : (
                <>
                  {" "}
                  <GreenFullCheckIcon />
                  <UpfrontPaymentReceiptThankYou>
                    {isPaymentPlan
                      ? t("upfrontPayment.receipt.paymentPlanThankYou")
                      : t("upfrontPayment.receipt.paymentThankYou")}
                  </UpfrontPaymentReceiptThankYou>{" "}
                </>
              )}
            </UpfrontPaymentReceiptHeader>
          </>
        ) : (
          <UpfrontPaymentReceiptTitle>
            {t("upfrontPayment.main.receipt")}
          </UpfrontPaymentReceiptTitle>
        )}
        {modal && (
          <UpfrontPaymentReceiptMessage>
            {showNotice ? (
              <>
                {GetTypographyContent({
                  content: (
                    <Trans i18nKey="upfrontPayment.receipt.eobNotice">
                      <span style={{ fontWeight: "bold", display: "contents" }}>
                        This is not a bill and no action is needed when you
                        receive your EOB from your insurance.
                      </span>
                    </Trans>
                  ),
                  size: "medium",
                  textColor: "black",
                })}
              </>
            ) : (
              <>
                {GetTypographyContent({
                  content: t("upfrontPayment.receipt.emailSent"),
                  size: "medium",
                  textColor: "black",
                })}
              </>
            )}
          </UpfrontPaymentReceiptMessage>
        )}
        {showNotice ? (
          <UpfrontPaymentReceiptMessage>
            <EOBIcon />
          </UpfrontPaymentReceiptMessage>
        ) : (
          <>
            {isPaymentPlan ? (
              paymentPlan && (
                <>
                  <UpfrontPaymentInfoBillContent marginBottom={8}>
                    <UpfrontPaymentPlanTitle>
                      <UpfrontPaymentCost>
                        {getCentsToDollars(paymentAmount)}
                      </UpfrontPaymentCost>
                      <UpfrontPaymentMainText>
                        {t("upfrontPayment.paymentPlan.splitMonthly")}
                      </UpfrontPaymentMainText>
                    </UpfrontPaymentPlanTitle>
                    <BabyPeekIntroFAQAccordionWrapper>
                      {t("upfrontPayment.paymentPlan.paymentCount", {
                        count: Number(numberPayments || 6),
                      })}
                    </BabyPeekIntroFAQAccordionWrapper>
                  </UpfrontPaymentInfoBillContent>
                  <UpfrontPaymentPlanSteps steps={paymentPlan} />
                  <UpfrontPaymentSolidLine />
                  <UpfrontPaymentInfoBillContent style={{ marginTop: "10px" }}>
                    <UpfrontPaymentMainText>
                      {t("upfrontPayment.paymentPlan.total")}
                    </UpfrontPaymentMainText>
                    <UpfrontPaymentMainText>
                      {getCentsToDollars(paymentAmount)}
                    </UpfrontPaymentMainText>
                  </UpfrontPaymentInfoBillContent>
                </>
              )
            ) : (
              <>
                {" "}
                <UpfrontPaymentInfoRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.receipt.dateOfService")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: finalReceipt?.date_of_service || "",
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.receipt.provider")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: finalReceipt?.provider_name || "",
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                </UpfrontPaymentInfoRow>
                <UpfrontPaymentInfoRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.receipt.amountPaid")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: `${getCentsToDollars(
                        finalReceipt?.amount_paid,
                      )}`,
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.receipt.paymentDate")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: finalReceipt?.payment_date || "",
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                </UpfrontPaymentInfoRow>
                <UpfrontPaymentInfoRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.editPaymentMethod.paymentMethod")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: `${finalReceipt?.card_brand || ""} - ${
                        finalReceipt?.last_four_digits || ""
                      }`,
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                  <UpfrontPaymentFieldRow>
                    <UpfrontPaymentFieldRowTitle>
                      {t("upfrontPayment.receipt.transactionId")}
                    </UpfrontPaymentFieldRowTitle>
                    {GetTypographyContent({
                      content: finalReceipt?.payment_intent_id || "",
                      size: "medium",
                      textColor: "black",
                    })}
                  </UpfrontPaymentFieldRow>
                </UpfrontPaymentInfoRow>{" "}
              </>
            )}
          </>
        )}
        {modal && setModal && (
          <UpfrontPaymentButtonRow>
            <Button
              type="submit"
              label={
                showNotice
                  ? t("upfrontPayment.actions.iUnderstand")
                  : t("upfrontPayment.actions.next")
              }
              size="medium"
              onClick={() => {
                if (showNotice) {
                  setModal(false);
                  setActiveStep(StepperIndex.Main);
                } else {
                  setShowNotice(true);
                }
              }}
              backgroundColor="greenDark"
            />
          </UpfrontPaymentButtonRow>
        )}
      </UpfrontPaymentReceiptWrapper>
    </div>
  );
};
